<template>
  <v-app-bar
    app
    flat
    color="background"
    style="z-index: 10"
    :height="deviceSize.mobile ? '54px' : '66px'"
    class="main-nav-bar"
    :class="{ mobile: deviceSize.mobile }"
  >
    <v-fade-transition
      group
      leave-absolute
      tag="div"
      class="d-flex align-center justify-space-between"
      style="width: 100% !important; padding: 6px 4px !important"
      duration="50"
    >
      <v-avatar
        v-if="deviceSize.mobile"
        tile
        class="rounded-lg d-flex grow"
        key="wordmark"
        style="width: 150px !important; max-width: 150px !important"
      >
        <v-img
          lazy-src="/img/wordmark_filled.svg"
          max-height="40px"
          max-width="150px"
          src="/img/wordmark_filled.svg"
          aspect-ratio="1"
        ></v-img>
      </v-avatar>
      <v-fade-transition
        tag="div"
        hide-on-leave
        group
        key="first-group"
        class="d-flex align-center"
        :class="{ 'justify-start': $vuetify.breakpoint.smAndUp }"
        style="transition-delay: 75ms !important; width: 100% !important"
        duration="50"
      >
        <StyledButton
          v-if="$vuetify.breakpoint.smAndUp && !miniNav"
          preset="action-stacked"
          icon="mdi-menu-open"
          color="background"
          textColor="icon"
          :dense="true"
          minWidth="48px"
          height="36px"
          label="Menu"
          rounded="lg"
          key="nav-close"
          :action="() => $emit('toggle-nav')"
          :text-transform="true"
          class="px-1"
        />
        <ais-instant-search
          v-if="$vuetify.breakpoint.smAndUp || searchVisible"
          :search-client="algolia"
          index-name="mytrace"
          :initial-ui-state="initialUiState"
          :future="{
            preserveSharedStateOnUnmount: true,
          }"
          key="search"
          class="d-flex"
          :class="{
            shrink: deviceSize.mobile,
            'justify-start': !deviceSize.mobile,
            'justify-end': deviceSize.mobile,
          }"
          style="width: clamp(325px, 85%, 500px) !important"
          v-click-outside="blurSearch"
        >
          <v-fade-transition
            tag="div"
            class="d-flex align-center justify-end shrink"
            style="
              gap: 8px;
              width: 100% !important;
              transition: all 0.1s ease !important;
            "
            leave-absolute
            hide-on-leave
            group
            key="search-group"
            id="algolia"
          >
            <v-menu
              v-model="menu"
              transition="slide-y-transition"
              ref="menu"
              key="menu"
              offset-y
              bottom
              origin="top right"
              :nudge-bottom="12"
              :close-on-content-click="false"
              close-on-click
            >
              <template v-slot:activator="{ on, attrs }">
                <ais-search-box key="search-box" style="width: 100% !important">
                  <template
                    v-slot="{ currentRefinement, isSearchStalled, refine }"
                  >
                    <v-text-field
                      @input="handleSearch(refine, currentRefinement)"
                      @keyup.esc="blurSearch"
                      @focus="on.click"
                      @click="on.click"
                      @blur="blurSearch"
                      autocomplete="off"
                      v-model="currentRefinement"
                      hide-details
                      rounded
                      dense
                      clearable
                      full-width
                      ref="algolia"
                      id="algolia-search"
                      prepend-inner-icon="mdi-magnify"
                      class="algolia-search secondaryBackground"
                      @keydown="
                        handleKeyboardSearchNavigation(null, null, $event)
                      "
                      style="width: 100% !important"
                      :style="{
                        border: `1px solid ${$vuetify.theme.currentTheme.background3} !important`,
                      }"
                      tabindex="0"
                    >
                      <template v-slot:label>
                        <div
                          style="
                            height: 100%;
                            width: 100% !important;
                            min-width: 200px !important;
                            min-height: 22px !important;
                          "
                        >
                          <span class="mr-2">Search Accounts</span>
                          <kbd
                            v-if="!deviceSize.mobile"
                            class="sheet secondary primary--text"
                            style="
                              margin-top: -2px !important;
                              border-radius: 6px !important;
                            "
                          >
                            <span
                              class="font-weight-bold text-body-2 d-inline-flex align-center"
                              style="line-height: normal !important"
                            >
                              <v-icon size="10" class="mr-1 primary--text">{{
                                metaKey.icon
                              }}</v-icon
                              >{{
                                keyboardShortcuts.accountSearch.keys[1]
                              }}</span
                            >
                          </kbd>
                        </div>
                      </template>
                    </v-text-field>
                  </template>
                </ais-search-box>
              </template>
              <v-card
                class="rounded-lg d-flex grow"
                style="width: clamp(250px, 100%, 100%) !important"
              >
                <v-list style="width: 100%">
                  <ais-hits>
                    <template v-slot="{ items, sendEvent }">
                      <v-list-item-group v-model="accountSearch">
                        <v-list-item
                          v-if="loading"
                          key="noData"
                          class="d-flex justify-center align-center"
                        >
                          <div class="d-flex justify-center">
                            <div
                              class="d-flex flex-row align-center justify-center"
                            >
                              <v-progress-circular
                                :width="3"
                                size="15"
                                color="loading"
                                indeterminate
                              ></v-progress-circular>
                              <v-list-item-subtitle class="text--disabled ml-2"
                                >Loading results...</v-list-item-subtitle
                              >
                            </div>
                          </div>
                        </v-list-item>
                        <v-list-item v-else-if="items.length === 0">
                          <v-list-item-content>
                            <v-list-item-subtitle class="text-center">
                              <span class="text-wrap">No records found.</span>
                            </v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>
                        <span
                          v-else
                          v-for="(item, i) in items"
                          :key="item.objectID"
                        >
                          <v-divider
                            v-if="i !== 0"
                            :key="`${i}-divider`"
                          ></v-divider>
                          <AccountCell
                            :account="item"
                            :key="item.objectID"
                            :value="item.objectID"
                            :fetch-logo="fetchLogo"
                            @keydown-emit="
                              (event) =>
                                handleKeyboardSearchNavigation(i, items, event)
                            "
                            ref="searchResult"
                          />
                        </span>
                      </v-list-item-group>
                    </template>
                  </ais-hits>
                  <ais-state-results>
                    <template
                      v-slot="{ state: { query }, results: { hits, nbPages } }"
                    >
                      <div v-if="!loading && nbPages > 1" class="py-3">
                        <ais-pagination
                          :show-first="false"
                          :show-last="false"
                          :padding="2"
                          :class-names="{
                            'ais-Pagination': 'MyCustomPagination',
                            'ais-Pagination-item': 'MyCustomPaginationList',
                            // ...
                          }"
                        >
                          <template
                            v-slot="{
                              currentRefinement,
                              nbPages,
                              pages,
                              isFirstPage,
                              isLastPage,
                              refine,
                              createURL,
                            }"
                          >
                            <v-pagination
                              @input="(value) => refine(value)"
                              color="review"
                              v-model="currentRefinement"
                              :length="nbPages - 1"
                              total-visible="7"
                            ></v-pagination>
                          </template>
                        </ais-pagination>
                      </div>
                      <div v-else />
                    </template>
                  </ais-state-results>
                </v-list>
              </v-card>
            </v-menu>
          </v-fade-transition>
        </ais-instant-search>
      </v-fade-transition>
      <div
        class="d-flex align-center mr-1"
        v-if="$vuetify.breakpoint.smAndUp || !searchVisible"
        key="actions"
        style="gap: 24px"
      >
        <template v-if="$vuetify.breakpoint.smAndUp">
          <div
            v-if="downloadingUpdate"
            class="text-caption font-weight-bold text--secondary d-flex align-center justify-center text-no-wrap"
            style="gap: 6px"
            key="updates"
          >
            <v-progress-circular
              :width="2"
              size="10"
              color="loading"
              indeterminate
            ></v-progress-circular>
            Downloading Updates
          </div>
          <StyledButton
            v-else-if="updateExists"
            preset="outlined"
            label="Install Updates"
            icon="mdi-download"
            iconSize="14px"
            minHeight="36px"
            size="small"
            rounded="pill"
            key="updates-available"
            :action="onInstallUpdate"
          />
        </template>
        <div class="d-flex align-center" style="gap: 12px">
          <v-icon
            v-if="deviceSize.mobile && !searchVisible"
            color="icon"
            :size="deviceSize.mobile ? 24 : 28"
            @click.stop="handleSearchClick"
            >mdi-magnify</v-icon
          >
          <v-icon
            v-if="!searchVisible || $vuetify.breakpoint.smAndUp"
            color="icon"
            :size="deviceSize.mobile ? 24 : 28"
            @click="openSupportTicket"
            >mdi-help-circle-outline</v-icon
          >
          <v-badge
            v-if="!searchVisible || $vuetify.breakpoint.smAndUp"
            overlap
            bordered
            color="error"
            :content="alerts"
            :value="alerts"
          >
            <v-icon
              @click="viewNotifications"
              :size="deviceSize.mobile ? 24 : 28"
              >mdi-bell-outline</v-icon
            >
          </v-badge>
        </div>
        <v-menu
          v-if="!searchVisible || $vuetify.breakpoint.smAndUp"
          bottom
          min-width="200px"
          class="rounded-lg"
          offset-y
          :nudge-bottom="6"
          key="menu"
        >
          <template v-slot:activator="{ on }">
            <v-btn
              icon
              :height="deviceSize.mobile ? 36 : 40"
              :width="deviceSize.mobile ? 36 : 40"
              v-on="on"
              key="menu-activator"
            >
              <v-avatar color="review" :size="deviceSize.mobile ? 36 : 40">
                <span
                  class="text-uppercase font-weight-medium white--text"
                  :style="`font-size: 0.95rem;
                            letter-spacing: 0.0125em;`"
                >
                  {{ getUser.initials }}
                </span>
              </v-avatar>
            </v-btn>
          </template>
          <v-card class="rounded-lg">
            <v-list-item-content class="justify-center pb-0 rounded-lg">
              <div class="mx-auto text-center rounded-lg">
                <v-avatar color="review" class="my-2">
                  <span class="text-uppercase text-h6 white--text">
                    {{ getUser.initials }}
                  </span>
                </v-avatar>
                <h3 class="text-capitalize">
                  {{ `${getUser?.name?.first} ${getUser?.name?.last}` }}
                </h3>
                <p class="text-caption">
                  <span>{{ getUser.email }}</span>
                  <span class="text-caption d-block">
                    {{
                      getUser.phoneNumber?.length > 0
                        ? $options.filters.phoneNumber(getUser.phoneNumber)
                        : "-"
                    }}
                  </span>
                </p>
                <div class="d-flex justify-space-between align-center mb-3">
                  <div class="d-flex align-center pl-4">
                    <v-icon left small> mdi-theme-light-dark </v-icon>
                    <span
                      class="text-body-2 font-weight-medium text--secondary d-flex text-center justify-center align-center"
                      >Dark Mode</span
                    >
                  </div>
                  <v-switch
                    hide-details
                    :input-value="$vuetify.theme.dark"
                    color="review"
                    inset
                    dense
                    class="mt-0 pt-0"
                    @change="toggleTheme"
                    @click.stop
                  ></v-switch>
                </div>
                <v-btn
                  v-if="getUser.accountType >= 2"
                  depressed
                  block
                  text
                  @click="traverse('profile-view')"
                >
                  <v-icon small left>mdi-account</v-icon>
                  <span class="text-capitalize">profile</span>
                </v-btn>
                <p class="text-caption mt-2 mb-0" style="opacity: 0.6">
                  {{ getAppName }} v26.3.05
                </p>
                <v-divider class="mt-3"></v-divider>
                <v-btn
                  depressed
                  block
                  text
                  class="rounded-b-lg py-6"
                  @click="$emit('logout')"
                >
                  <v-icon small left>mdi-logout</v-icon>
                  <span class="text-capitalize">log out</span>
                </v-btn>
              </div>
            </v-list-item-content>
          </v-card>
        </v-menu>
      </div>
    </v-fade-transition>
  </v-app-bar>
</template>

<script>
// Libraries
import { mapGetters, mapActions } from "vuex";
import algoliasearch from "algoliasearch/lite";
import {
  // Firestore
  firestore,
  getDoc,
  doc,
  // Storage
  storage,
  ref,
  getDownloadURL,
} from "@/firebase/init";
// Components

const themeChannel = new BroadcastChannel("theme-channel");

export default {
  name: "AppBar",
  data() {
    return {
      searchVisible: false,
      menu: false,
      loading: false,
      debouncedRefineSearch: null,
      accountSearch: null,
      algolia: algoliasearch(
        process.env.VUE_APP_ALGOLIA_APPID,
        process.env.VUE_APP_ALGOLIA_API,
      ),
      initialUiState: {
        mytrace: {
          query: "",
        },
      },
    };
  },
  props: {
    downloadingUpdate: {
      default: false,
      type: Boolean,
      required: true,
    },
    updateExists: {
      default: false,
      type: Boolean,
      required: true,
    },
    miniNav: {
      default: false,
      type: Boolean,
      required: true,
    },
    alerts: {
      default: 0,
      type: Number,
      required: true,
    },
  },
  methods: {
    ...mapActions([
      "_user",
      "_activityDialog",
      "_notification",
      "_email",
      "_dialpadFeatureFlag",
      "_rightDrawer",
      "_alerts",
    ]),
    onInstallUpdate() {
      this.$emit("install-update");
    },
    /* API */
    // GET
    async fetchLogo(accountID, cacheManager) {
      try {
        const cachedLogo = cacheManager.get(accountID);
        if (cachedLogo || cacheManager.hasKey(accountID)) {
          return cachedLogo ?? null; // Use cached logo if available
        }
        // Fetch logo details from Firestore
        const accountsDoc = doc(firestore, "accounts", accountID);
        const response = await getDoc(accountsDoc);
        if (!response.exists() || !response.data().logo) {
          cacheManager.set(accountID, null);
          return null;
        }

        const mediaDoc = doc(firestore, "media", response.data().logo);
        const mediaResponse = await getDoc(mediaDoc);
        if (!mediaResponse.exists()) {
          cacheManager.set(accountID, null);
          return null;
        }

        const path = mediaResponse.data().path;
        const result = await getDownloadURL(ref(storage, path));

        cacheManager.set(accountID, result);
        return result;
      } catch (error) {
        console.error("Error fetching logo:", error);
        return null;
      }
    },
    // POST
    /* */
    handleSearchClick() {
      this.searchVisible = true;
      console.log("Search Clicked");
      console.log(this.searchVisible);
      this.$nextTick(() => {
        // setTimeout(() => {
        this.$refs.algolia.focus();
        // document.getElementById("algolia-search").focus();
        // }, 200);
      });
    },
    handleSearch(refine, currentRefinement) {
      // Cancel any pending search FIRST
      if (this.debouncedRefineSearch?.cancel) {
        this.debouncedRefineSearch.cancel();
      }

      if (currentRefinement?.length > 0) {
        this.menu = true;
        this.loading = true;

        // Create a new debounced function for this search
        this.debouncedRefineSearch = _.debounce(async () => {
          await refine(currentRefinement);
          this.loading = false;
        }, 750);

        this.debouncedRefineSearch();
      } else {
        // Clear immediately
        refine("");
        this.menu = false;
        this.loading = false;
      }
    },
    refineSearch: _.debounce(async function (refine, currentRefinement) {
      await refine(currentRefinement);
      this.loading = false;
    }, 750),
    blurSearch() {
      if (this.deviceSize.mobile && this.searchVisible) {
        this.menu = false;
      }
    },
    toggleTheme() {
      this.$vuetify.theme.dark = !this.$vuetify.theme.dark;
      localStorage.setItem(
        "theme",
        this.$vuetify.theme.dark ? "dark" : "light",
      );
      themeChannel.postMessage({
        theme: this.$vuetify.theme.dark ? "dark" : "light",
      });
      const metaThemeColor = document.querySelector("meta[name=theme-color]");
      metaThemeColor.setAttribute(
        "content",
        this.$vuetify.theme.currentTheme.background,
      );
    },
    async handleKeyboardSearchNavigation(i, items, event) {
      let isArrowKey = ["ArrowDown", "ArrowUp"].includes(event.key);
      if (isArrowKey) {
        // Prevent default scrolling behavior
        event.preventDefault();
        event.stopPropagation();

        if (event.key === "ArrowDown") {
          // Increment index, but keep it within bounds
          this.focusedSearchIndex = !items?.length
            ? 0
            : (this.focusedSearchIndex + 1) % this.$refs.searchResult.length;
        } else if (event.key === "ArrowUp") {
          // Decrement index, wrap around if necessary
          this.focusedSearchIndex = !items?.length
            ? this.$refs.searchResult.length - 1
            : (this.focusedSearchIndex - 1 + this.$refs.searchResult.length) %
              this.$refs.searchResult.length;
        }
        let foundItem = null;
        if (items?.length) {
          foundItem = this.$refs.searchResult.find(
            (result) =>
              result.account?.objectID ===
              items[this.focusedSearchIndex].objectID,
          );
        } else {
          foundItem = this.$refs.searchResult[0];
        }
        this.$nextTick(() => {
          let navItem =
            foundItem ?? this.$refs.searchResult[this.focusedSearchIndex];
          if (navItem?.$el) {
            navItem.$el.focus();
          }
        });
      }
    },
    openSupportTicket() {
      window.open(
        "https://mytrace-support-tickets.web.app/",
        "_blank",
        "noopener,noreferrer",
      );
    },
    viewNotifications() {
      this._rightDrawer({
        active: true,
        title: "Notifications",
        type: "notifications",
        data: {},
      });
    },
    async traverse(action, params) {
      let link,
        relatedToResource = "";
      switch (action) {
        case "profile-view":
          this.$router.push({
            name: "profile-view",
            params: {
              id: this.getUser.uid,
            },
          });
          break;
      }
    },
  },
  computed: {
    ...mapGetters([
      // Main
      "getUser",
      "getAppName",
    ]),
  },
};
</script>

<style scoped>
.main-nav-bar >>> .v-toolbar__content {
  padding: 6px 8px !important;
}

.main-nav-bar.mobile >>> .v-toolbar__content {
  padding: 6px 0px !important;
}

.algolia-search >>> .v-input__slot {
  padding: 0px 16px !important;
}

.algolia-search >>> .v-text-field__slot {
  align-items: center !important;
  min-height: 100%;
}

.algolia-search >>> * {
  cursor: pointer !important;
}

.algolia-search >>> .v-text-field__slot > label {
  display: flex;
  align-items: center;
  min-height: fit-content !important;
  position: unset !important;
  min-width: fit-content !important;
  padding: 4px 0px !important;
}
</style>
